// The order portal's API client and the wire types it consumes — hand-
// authored against server/src/models/*.rs (the shapes the testbed proves:
// online_checkout, customer_me). Public reads carry no token; the checkout
// and the account ride the anonymous customer identity (auth.ts). Relative
// /api/v1 base: Vite proxies it to the slot's API in dev, the hosting
// rewrite does in production.

import { adoptIdentity, customerToken } from './auth';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

type Params = Record<string, string | number | boolean | undefined>;

async function request<T>(method: string, path: string, opts: { body?: unknown; params?: Params; auth?: boolean } = {}): Promise<T> {
  const url = new URL('/api/v1' + path, window.location.origin);
  for (const [k, v] of Object.entries(opts.params ?? {})) if (v !== undefined) url.searchParams.set(k, String(v));
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.auth) headers.Authorization = `Bearer ${await customerToken()}`;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  const resp = await fetch(url, { method, headers, body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined });
  if (!resp.ok) {
    let code = 'unknown';
    let message = resp.statusText;
    try {
      const e = ((await resp.json()) as { error?: { code?: string; message?: string } })?.error;
      code = e?.code ?? code;
      message = e?.message ?? message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(resp.status, code, message);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export const api = {
  /** a public read */
  get: <T>(path: string, params?: Params) => request<T>('GET', path, { params }),
  /** a read as the customer identity */
  getMine: <T>(path: string, params?: Params) => request<T>('GET', path, { params, auth: true }),
  /** a write as the customer identity (auth: false for the public doors — quote, delivery quotes) */
  post: <T>(path: string, body?: unknown, opts: { auth?: boolean } = {}) => request<T>('POST', path, { body: body ?? {}, auth: opts.auth ?? true }),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, { body, auth: true }),
  del: <T>(path: string) => request<T>('DELETE', path, { auth: true }),
};

export const storeBase = (rid: string, sid: string) => `/restaurants/${rid}/stores/${sid}`;

// ── the store ───────────────────────────────────────────────────────

export type Channel = 'pickup' | 'delivery' | 'dine_in';

export const CHANNEL_LABEL: Record<Channel, string> = { pickup: 'Pickup', delivery: 'Delivery', dine_in: 'Dine-in' };

export type StorePublic = {
  restaurantId: string;
  storeId: string;
  name: string;
  timezone: string;
  address: string;
  phoneE164: string;
  pickupEnabled: boolean;
  deliveryEnabled: boolean;
  dineInEnabled: boolean;
  temporarilyClosed: boolean;
  permanentlyClosed: boolean;
  /** Stripe Connect onboarded with charges enabled */
  onlinePaymentsEnabled: boolean;
};

export type HoursWindow = { dayOfWeek: number; sessionNo: number; isOpen: boolean; openTime: string | null; closeTime: string | null };
/** `leadMinutes`: how far ahead of now a scheduled order on this channel must be (null for pos). */
export type ScopeHours = { scope: 'pos' | Channel; windows: HoursWindow[]; openNow: boolean; leadMinutes: number | null };
export type Holiday = { holidayDate: string; label: string; isClosed: boolean };
/** `scheduleAheadDays`: a slot's store-local date is at most today + this; `closedHolidays`: from today on, no slots. */
export type HoursView = { timezone: string; paused: boolean; scopes: ScopeHours[]; scheduleAheadDays: number; closedHolidays: Holiday[] };

export function openNowFor(hours: HoursView | null, channel: Channel): boolean {
  if (!hours || hours.paused) return false;
  return hours.scopes.find((s) => s.scope === channel)?.openNow ?? false;
}

// ── store-local time ────────────────────────────────────────────────
// Hours are wall-clock times in the STORE's timezone (HoursView.timezone),
// never the browser's: a customer abroad, or a phone on the wrong zone,
// still sees the kitchen's own clock. No date library — Intl does the
// zone arithmetic.

type Ymd = { y: number; m: number; d: number };

/** The store-local calendar date and wall time of an instant. */
function zonedParts(t: number, tz: string): Ymd & { hh: number; mm: number } {
  const parts = new Intl.DateTimeFormat('en-AU', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' }).formatToParts(t);
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { y: n('year'), m: n('month'), d: n('day'), hh: n('hour'), mm: n('minute') };
}

/** The instant a store-local wall time names (DST-aware: re-checks the offset at the answer). */
function zonedInstant(day: Ymd, hh: number, mm: number, tz: string): number {
  const wall = Date.UTC(day.y, day.m - 1, day.d, hh, mm);
  const offsetAt = (t: number) => {
    const p = zonedParts(t, tz);
    return Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm) - Math.floor(t / 60_000) * 60_000;
  };
  let t = wall - offsetAt(wall);
  t = wall - offsetAt(t);
  return t;
}

const addDays = (day: Ymd, n: number): Ymd => {
  const t = new Date(Date.UTC(day.y, day.m - 1, day.d + n));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
};
/** 1 = Monday, as the server counts. */
const isoDow = (day: Ymd): number => ((new Date(Date.UTC(day.y, day.m - 1, day.d)).getUTCDay() + 6) % 7) + 1;
const hhmm = (s: string): [number, number] => s.split(':').map(Number) as [number, number];

/** "Today" / "Tomorrow" / "Wed" — a store-local date against the store-local today. */
function dayLabel(today: Ymd, day: Ymd): string {
  const n = (Date.UTC(day.y, day.m - 1, day.d) - Date.UTC(today.y, today.m - 1, today.d)) / 86_400_000;
  return n === 0 ? 'Today' : n === 1 ? 'Tomorrow' : new Date(Date.UTC(day.y, day.m - 1, day.d, 12)).toLocaleDateString('en-AU', { weekday: 'short', timeZone: 'UTC' });
}

/** A store-local wall time, "9:30pm". */
function wallLabel(hh: number, mm: number): string {
  return `${hh % 12 === 0 ? 12 : hh % 12}:${String(mm).padStart(2, '0')}${hh < 12 ? 'am' : 'pm'}`;
}

/** Today's (store-local) closing time for a channel, "9:30pm" — null when closed today. */
export function closesAt(hours: HoursView | null, channel: Channel, now = Date.now()): string | null {
  const scope = hours?.scopes.find((s) => s.scope === channel);
  if (!hours || !scope) return null;
  const today = zonedParts(now, hours.timezone);
  const rows = scope.windows.filter((w) => w.dayOfWeek === isoDow(today) && w.isOpen && w.closeTime);
  if (rows.length === 0) return null;
  return wallLabel(...hhmm(rows.map((w) => w.closeTime!).sort().at(-1)!));
}

const SLOT_STEP_MINUTES = 15;
const ymdKey = (day: Ymd) => `${day.y}-${String(day.m).padStart(2, '0')}-${String(day.d).padStart(2, '0')}`;
const daysBetween = (a: Ymd, b: Ymd) => Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86_400_000);

/** Time slots for a channel, judged as the server judges a requestedFor
 * (trading_rules.rs judge_slot): 15-minute steps inside the channel's
 * windows, at least its lead time from now, on a store-local date no later
 * than today + the schedule-ahead days, never on a closed holiday — all on
 * the store's clock. */
export function slotsFor(hours: HoursView | null, channel: Channel, now = Date.now()): { value: string; label: string }[] {
  const scope = hours?.scopes.find((s) => s.scope === channel);
  if (!hours || !scope) return [];
  const tz = hours.timezone;
  const out: { value: string; label: string }[] = [];
  const earliest = now + (scope.leadMinutes ?? 0) * 60_000;
  const closed = new Set(hours.closedHolidays.filter((h) => h.isClosed).map((h) => h.holidayDate));
  const today = zonedParts(now, tz);
  // the day before today too: its overnight window may spill into this morning
  for (let d = -1; d <= hours.scheduleAheadDays; d++) {
    const day = addDays(today, d);
    const windows = scope.windows.filter((w) => w.dayOfWeek === isoDow(day) && w.isOpen && w.openTime && w.closeTime).sort((a, b) => (a.openTime! < b.openTime! ? -1 : 1));
    for (const w of windows) {
      const [oh, om] = hhmm(w.openTime!);
      const [ch, cm] = hhmm(w.closeTime!);
      const start = zonedInstant(day, oh, om, tz);
      // a window that closes at or before it opens runs past midnight
      const end = ch * 60 + cm > oh * 60 + om ? zonedInstant(day, ch, cm, tz) : zonedInstant(addDays(day, 1), ch, cm, tz);
      for (let t = start; t < end; t += SLOT_STEP_MINUTES * 60_000) {
        if (t < earliest) continue;
        // the slot's own store-local date decides the horizon and the holiday
        const local = zonedParts(t, tz);
        if (daysBetween(today, local) > hours.scheduleAheadDays || closed.has(ymdKey(local))) continue;
        out.push({ value: new Date(t).toISOString(), label: `${dayLabel(today, local)} ${wallLabel(local.hh, local.mm)}` });
      }
    }
  }
  // a week of quarter-hours fits; a store scheduling months ahead lists its first ~5 days
  return out.slice(0, 500);
}

// ── the composed menu (GET …/menu?channel=) ─────────────────────────

export type Price = { priceInCents: number; pickupExtraInCents: number; deliveryExtraInCents: number; dineInExtraInCents: number; uberEatsExtraInCents: number };
export type SizeBase = { baseId: string; extraChargeInCents: number };
export type PizzaSize = { sizeId: string; name: string; halfAndHalfEnabled: boolean; halfAndHalfExtraChargeInCents: number; sortOrder: string; bases: SizeBase[] };
export type PizzaBase = { baseId: string; name: string; docketLabel: string | null; sortOrder: string };
export type PizzaTopping = { toppingId: string; name: string; soldOut: boolean; sortOrder: string; prices: { sizeId: string; priceInCents: number }[] };
export type ModifierOption = { optionId: string | null; name: string; extraChargeInCents: number; isDefault: boolean };
export type Modifier = { modifierId: string; name: string; minSelect: number; maxSelect: number; sortOrder: string; options: ModifierOption[] };
export type PizzaSizePrice = { sizeId: string; price: Price; halfAndHalfAvailable: boolean };
export type ItemModifier = { modifierId: string; minSelectOverride: number | null; maxSelectOverride: number | null };
export type SlotOption = { itemId: string; extraChargeInCents: number; isDefault: boolean };
export type BundleSlot = {
  slotId: string | null;
  slotType: 'generic' | 'pizza';
  name: string;
  minSelect: number;
  maxSelect: number;
  allowRepeat: boolean;
  allowHalfAndHalf: boolean | null;
  sizeId: string | null;
  options: SlotOption[];
};

/** The type-specific half, tagged by `itemType` and flattened into the item. */
export type ItemDefinition =
  | { itemType: 'generic'; price: Price }
  | { itemType: 'pizza'; sizes: PizzaSizePrice[]; defaultToppings: string[] }
  | { itemType: 'half_and_half'; sizeId: string }
  | { itemType: 'bundle'; price: Price | null; slots: BundleSlot[] };

export type ItemType = ItemDefinition['itemType'];

export type Unorderable = 'unavailable' | 'not_on_this_channel' | 'sold_out' | 'no_sizes' | 'half_and_half_disabled' | 'too_few_halves' | 'slot_cannot_be_filled';

/** A menu item as the composed menu serves it: the item, flattened, plus the channel's verdicts. */
export type MenuItemView = {
  itemId: string;
  itemClass: 'single' | 'bundle';
  categoryId: string | null;
  name: string;
  description: string | null;
  soldOut: boolean;
  available: boolean;
  availablePickup: boolean;
  availableDelivery: boolean;
  availableDineIn: boolean;
  isGstApplicable: boolean;
  isAlcohol: boolean;
  imageUrl: string | null;
  sortOrder: string;
  modifiers: ItemModifier[];
  /** generic and bundle: the price on this channel */
  channelPriceInCents: number | null;
  /** pizza: [sizeId, channel price] in size order */
  sizePrices: [string, number][];
  /** half-and-half: the pizzas that may fill a half at the pinned size */
  eligiblePizzaIds: string[];
  orderable: boolean;
  unorderableReason: Unorderable | null;
} & ItemDefinition;

export type MenuCategoryView = { categoryId: string; name: string; description: string | null; imageUrl: string | null; backgroundColor: string | null; sortOrder: string; items: MenuItemView[] };

export type MenuDocument = {
  channel: Channel | 'uber_eats';
  categories: MenuCategoryView[];
  /** items without a category: slot options and the like */
  unlistedItems: MenuItemView[];
  sizes: PizzaSize[];
  bases: PizzaBase[];
  toppings: PizzaTopping[];
  modifiers: Modifier[];
};

/** Every item the document carries, by id — slot options and halves live here too. */
export function itemsById(menu: MenuDocument): Map<string, MenuItemView> {
  const m = new Map<string, MenuItemView>();
  for (const c of menu.categories) for (const i of c.items) m.set(i.itemId, i);
  for (const i of menu.unlistedItems) m.set(i.itemId, i);
  return m;
}

/** Rank order is bytewise — never localeCompare. */
export const byRank = <T extends { sortOrder: string }>(a: T, b: T): number => (a.sortOrder < b.sortOrder ? -1 : a.sortOrder > b.sortOrder ? 1 : 0);

export function sizePriceOf(view: MenuItemView, sizeId: string): number | null {
  return view.sizePrices.find(([s]) => s === sizeId)?.[1] ?? null;
}

/** The menu card's price: the least a configuration of this item can cost on the channel. */
export function cardPrice(view: MenuItemView, menu: MenuDocument, all: Map<string, MenuItemView>): { cents: number; from: boolean } | null {
  if (view.itemType === 'pizza') {
    if (view.sizePrices.length === 0) return null;
    return { cents: Math.min(...view.sizePrices.map(([, c]) => c)), from: view.sizePrices.length > 1 };
  }
  if (view.itemType === 'half_and_half') {
    const size = menu.sizes.find((s) => s.sizeId === view.sizeId);
    const halves = view.eligiblePizzaIds.map((id) => all.get(id)).filter((p): p is MenuItemView => !!p).map((p) => sizePriceOf(p, view.sizeId)).filter((c): c is number => c !== null);
    if (!size || halves.length === 0) return null;
    // the server's rule: the dearest half + the size's split charge
    return { cents: Math.min(...halves) + size.halfAndHalfExtraChargeInCents, from: true };
  }
  if (view.channelPriceInCents === null) return null;
  const from = view.itemType === 'bundle' && view.slots.some((s) => s.options.some((o) => o.extraChargeInCents > 0));
  return { cents: view.channelPriceInCents, from };
}

// ── the cart on the wire (models/orders.rs Cart) ────────────────────

export type WireModifier = { modifierId: string; optionId: string };
export type WireToppings = { add: { toppingId: string; quantity: number }[]; remove: string[] };
export type WirePizza = { itemId: string; toppings: WireToppings; modifiers: WireModifier[] };
export type WirePick =
  | { pickType: 'generic'; itemId: string; modifiers: WireModifier[] }
  | ({ pickType: 'pizza'; baseId: string | null } & WirePizza)
  | { pickType: 'half_and_half'; baseId: string | null; first: WirePizza; second: WirePizza };
/** CartSlotPick: the pick is flattened in — `pickType` sits beside `slotId`. */
export type WireSlotPick = { slotId: string; quantity: number } & WirePick;
export type WireItem =
  | { itemType: 'generic'; itemId: string; modifiers: WireModifier[] }
  | ({ itemType: 'pizza'; sizeId: string; baseId: string | null } & WirePizza)
  | { itemType: 'half_and_half'; itemId: string; baseId: string | null; first: WirePizza; second: WirePizza; modifiers: WireModifier[] }
  | { itemType: 'bundle'; itemId: string; picks: WireSlotPick[]; modifiers: WireModifier[] };
/** CartLine: quantity + comments, the item flattened in. */
export type WireLine = { quantity: number; comments?: string | null } & WireItem;

export type WireDiscount = { discountType: 'coupon'; label: string; couponCode: string };
export type WireDelivery = { addressLine: string; suburb: string; postcode: string; state: string; lat: number | null; lng: number | null };
export type WireCart = {
  source: 'website';
  collectionType: Channel;
  requestedFor?: string | null;
  lines: WireLine[];
  discounts?: WireDiscount[];
  customer?: { customerId?: string | null; name: string; phoneE164: string };
  delivery?: WireDelivery;
  dineIn?: { tableId: string; tableName?: string | null; guestCount?: number | null };
  comments?: string | null;
  redeemLoyalty?: boolean;
  clientOrderId?: string;
};

// ── what the server says it costs, and the order it becomes ─────────

export type OrderStatus = 'new' | 'accepted' | 'rejected' | 'cancelled' | 'completed' | 'in_progress';
export type PaymentState = 'unpaid' | 'partially_paid' | 'paid' | 'partially_refunded' | 'refunded';

export type ChosenModifier = { rowId: string; modifierName: string; optionName: string; extraChargeInCents: number };
export type OrderToppingRow = { rowId: string; action: 'default' | 'add' | 'remove'; toppingName: string; quantity: number; unitPriceInCents: number; totalPriceInCents: number; toppingId: string };
export type OrderPizza = { pizzaId: string; name: string; sizeName: string; baseName: string | null; unitPriceInCents: number; baseExtraInCents: number; itemId: string; sizeId: string; baseId: string | null; toppings: OrderToppingRow[]; modifiers: ChosenModifier[] };
export type OrderHalfAndHalf = { halfAndHalfId: string; sizeName: string; baseName: string | null; baseExtraInCents: number; splitExtraInCents: number; first: OrderPizza; second: OrderPizza };
export type BundlePick = { pickId: string; slotName: string; name: string | null; quantity: number; itemPriceInCents: number; extraChargeInCents: number; slotId: string; itemId: string | null; pickType: 'generic' | 'pizza' | 'half_and_half'; pizza?: OrderPizza; halfAndHalf?: OrderHalfAndHalf; modifiers?: ChosenModifier[] };
export type OrderLine = {
  orderItemId: string;
  name: string;
  description: string | null;
  quantity: number;
  comments: string | null;
  unitPriceInCents: number;
  lineTotalInCents: number;
  discountInCents: number;
  isGstApplicable: boolean;
  itemId: string | null;
  modifiers: ChosenModifier[];
  itemType: 'generic' | 'custom' | 'pizza' | 'half_and_half' | 'bundle';
  pizza?: OrderPizza;
  halfAndHalf?: OrderHalfAndHalf;
  picks?: BundlePick[];
};
export type OrderDiscount = { rowId: string; discountType: 'coupon' | 'loyalty' | 'preset' | 'custom'; label: string; amountInCents: number; couponCode: string | null };
export type OrderSurcharge = { rowId: string; surchargeType: string; label: string; amountInCents: number };
export type OrderTotals = { subtotalInCents: number; discountTotalInCents: number; surchargeTotalInCents: number; deliveryFeeInCents: number; gstInCents: number; grandTotalInCents: number };

export type Quote = { channel: Channel; lines: OrderLine[]; discounts: OrderDiscount[]; surcharges: OrderSurcharge[] } & OrderTotals;

export type Order = {
  orderId: string;
  source: string;
  collectionType: Channel;
  status: OrderStatus;
  statusReason: string | null;
  placedAt: string;
  requestedFor: string | null;
  customerId: string | null;
  customerName: string | null;
  docketLabel: string | null;
  comments: string | null;
  paymentState: PaymentState;
  paidTotalInCents: number;
  delivery: { addressLine: string; suburb: string; chargeInCents: number; trackingUrl: string | null } | null;
  dineIn: { tableId: string | null; tableName: string; guestCount: number | null } | null;
  lines: OrderLine[];
  discounts: OrderDiscount[];
  surcharges: OrderSurcharge[];
} & OrderTotals;

/** The public tracking window — the unguessable order id is the capability. */
export type TrackView = {
  orderId: string;
  status: OrderStatus;
  statusReason: string | null;
  collectionType: Channel;
  placedAt: string;
  requestedFor: string | null;
  etaMinutes: number | null;
  docketLabel: string | null;
  grandTotalInCents: number;
  paymentState: PaymentState;
  storeName: string;
  storePhoneE164: string;
  storeAddress: string;
  courierStatus: string | null;
  trackingUrl: string | null;
};

export type PaymentIntentView = { paymentId: string; paymentIntentId: string; clientSecret: string; publishableKey: string };

// ── delivery, tables, offers, the account ───────────────────────────

export type AddressCandidate = { formatted: string; addressLine: string; suburb: string; state: string; postcode: string; lat: number; lng: number };
export type DeliveryQuote = { serviceable: boolean; areaId: string | null; areaName: string | null; deliveryChargeInCents: number; minOrderInCents: number | null };
export type PublicTable = { tableId: string; name: string; maxGuests: number | null };
export type PublicCoupon = { code: string; couponType: 'flat_amount' | 'percent' | 'free_delivery'; amountInCents: number | null; percentBp: number | null; minOrderInCents: number | null; appliesPickup: boolean; appliesDelivery: boolean; appliesDineIn: boolean; endsOn: string | null };
/** CustomerAddress: one entry in the customer's saved book; at most one default. */
export type CustomerAddress = { addressId: string; label: string | null; addressLine: string; suburb: string; state: string; postcode: string; lat: number | null; lng: number | null; notes: string | null; isDefault: boolean };
export type AddressInput = Omit<CustomerAddress, 'addressId'>;
export type Customer = { customerId: string; userId: string | null; name: string; phoneE164: string | null; email: string | null; marketingSmsOptIn: boolean; marketingEmailOptIn: boolean; addresses: CustomerAddress[] };
export type MyProfileInput = { name: string; phone: string; email?: string | null; marketingSmsOptIn?: boolean; marketingEmailOptIn?: boolean };
export type LoyaltyReward = { orderId: string; storeId: string; amountInCents: number; orderGrandTotalInCents: number; createdAt: string };
/** The open card: rewards bank per order until ordersPerCard; then it redeems whole (WireCart.redeemLoyalty). */
export type LoyaltyCard = { cardId: string; prevCardId: string | null; isOpen: boolean; rewards: LoyaltyReward[]; valueInCents: number; isRedeemable: boolean; ordersPerCard: number; createdAt: string };

// ── table bookings (models/bookings.rs) ─────────────────────────────

export type BookingStatus = 'new' | 'accepted' | 'rejected' | 'cancelled' | 'completed';
export type BookingSource = 'website' | 'pos';
export type BookingEvent = { seq: number; eventType: string; actorType: string; actorId: string | null; note: string | null; occurredAt: string };
export type Booking = {
  bookingId: string;
  status: BookingStatus;
  statusReason: string | null;
  source: BookingSource;
  /** store-local YYYY-MM-DD */
  bookingDate: string;
  /** store-local HH:MM */
  bookingTime: string;
  sessionNo: number | null;
  pax: number;
  customerId: string | null;
  customerName: string;
  customerPhoneE164: string;
  customerEmail: string | null;
  tableId: string | null;
  tableName: string | null;
  notes: string | null;
  placedAt: string;
  events: BookingEvent[];
  createdAt: string;
  updatedAt: string;
};
export type BookingSettings = { enabled: boolean; maxPaxPerBooking: number; capacityPerSession: number | null; leadMinutes: number; scheduleAheadDays: number };
export type ClosedDate = { closedOn: string; label: string | null };
/** The whole booking book: the rules, the bookable sessions per weekday (the hours shape), the closed dates. */
export type BookingConfig = { settings: BookingSettings; sessions: HoursWindow[]; closedDates: ClosedDate[] };
export type PlaceBookingInput = { bookingDate: string; bookingTime: string; pax: number; customerName: string; customerPhone: string; customerEmail?: string | null; notes?: string | null };

/** The typed refusals of the website path (services/booking_service.rs place). */
export type BookingRefusal = 'bookings_disabled' | 'pax_over_limit' | 'outside_sessions' | 'date_unavailable' | 'too_soon' | 'too_far_ahead' | 'session_full' | 'invalid_pax' | 'name_required' | 'invalid_phone' | 'invalid_time';

export const BOOKING_STATUS_LABEL: Record<BookingStatus, string> = {
  new: 'Waiting for the store',
  accepted: 'Confirmed',
  rejected: 'Declined',
  cancelled: 'Cancelled',
  completed: 'Seated',
};

/** Today and the minute of the day in the store's timezone — the clock the server books by. */
export function storeClock(tz: string, at = new Date()): { today: string; minute: number } {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(at);
  const p = (t: string) => parts.find((x) => x.type === t)?.value ?? '00';
  return { today: `${p('year')}-${p('month')}-${p('day')}`, minute: Number(p('hour')) * 60 + Number(p('minute')) };
}

const dayMs = 86_400_000;
const dateOf = (iso: string) => Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
const isoOf = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const minuteOf = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
const hhmmOf = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** 1 = Monday … 7 = Sunday, as the server counts, for a calendar date. */
export function dayOfWeek(iso: string): number {
  return ((new Date(dateOf(iso)).getUTCDay() + 6) % 7) + 1;
}

/** The bookable times of one date: 15-minute steps inside its open sessions, the lead time
 *  applied against the store's clock. Mirrors the server's session rule — a session that
 *  closes before it opens runs past midnight, so its early hours belong to the same date. Pure. */
export function bookableTimes(cfg: BookingConfig, date: string, clock: { today: string; minute: number }): string[] {
  const offset = Math.round((dateOf(date) - dateOf(clock.today)) / dayMs);
  const earliest = clock.minute + cfg.settings.leadMinutes; // minutes from today's midnight
  const out = new Set<number>();
  for (const s of cfg.sessions.filter((w) => w.dayOfWeek === dayOfWeek(date) && w.isOpen && w.openTime && w.closeTime)) {
    const open = minuteOf(s.openTime!);
    const close = minuteOf(s.closeTime!);
    const spans: [number, number][] = close < open ? [[open, 1440], [0, close]] : [[open, close]];
    for (const [from, to] of spans) {
      for (let m = Math.ceil(from / 15) * 15; m < to; m += 15) if (offset * 1440 + m >= earliest) out.add(m);
    }
  }
  return [...out].sort((a, b) => a - b).map(hhmmOf);
}

/** The bookable dates: today through the horizon, closed dates out, dates with no time left out. Pure. */
export function bookableDates(cfg: BookingConfig, clock: { today: string; minute: number }): string[] {
  const closed = new Set(cfg.closedDates.map((d) => d.closedOn));
  const out: string[] = [];
  for (let d = 0; d <= cfg.settings.scheduleAheadDays; d++) {
    const iso = isoOf(dateOf(clock.today) + d * dayMs);
    if (!closed.has(iso) && bookableTimes(cfg, iso, clock).length > 0) out.push(iso);
  }
  return out;
}

/** "Tue 21 Oct" for a calendar date. */
export function dateLabel(iso: string, opts: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' }): string {
  return new Date(dateOf(iso)).toLocaleDateString('en-AU', { ...opts, timeZone: 'UTC' });
}

/** "7:00 pm" for a store-local HH:MM. */
export function timeLabel(hhmm: string): string {
  const m = minuteOf(hhmm);
  const h = Math.floor(m / 60);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m % 60).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
}

// ── the calls ───────────────────────────────────────────────────────

export const fetchStore = (rid: string, sid: string) => api.get<StorePublic>(`${storeBase(rid, sid)}/public`);
export const fetchHours = (rid: string, sid: string) => api.get<HoursView>(`${storeBase(rid, sid)}/hours`);
export const fetchMenu = (rid: string, sid: string, channel: Channel) => api.get<MenuDocument>(`${storeBase(rid, sid)}/menu`, { channel });
export const fetchPublicTables = (rid: string, sid: string) => api.get<PublicTable[]>(`${storeBase(rid, sid)}/tables/public`);
export const fetchPublicCoupons = (rid: string, sid: string) => api.get<PublicCoupon[]>(`${storeBase(rid, sid)}/coupons/public`);
export const searchAddress = (rid: string, sid: string, q: string) => api.get<AddressCandidate[]>(`${storeBase(rid, sid)}/address-search`, { q });
export const deliveryQuote = (rid: string, sid: string, a: WireDelivery) => api.post<DeliveryQuote>(`${storeBase(rid, sid)}/delivery-quotes`, a, { auth: false });
export const quoteCart = (rid: string, sid: string, cart: WireCart) => api.post<Quote>(`${storeBase(rid, sid)}/orders/quote`, cart, { auth: false });
export const placeOrder = (rid: string, sid: string, cart: WireCart) => api.post<Order>(`${storeBase(rid, sid)}/orders`, cart);
export const createPaymentIntent = (rid: string, sid: string, orderId: string) => api.post<PaymentIntentView>(`${storeBase(rid, sid)}/orders/${orderId}/payment-intents`);
export const fetchTrack = (rid: string, sid: string, orderId: string) => api.get<TrackView>(`${storeBase(rid, sid)}/orders/${orderId}/track`);
export const fetchMe = (rid: string) => api.getMine<Customer | null>(`/restaurants/${rid}/customer/me`);
// ── the verified phone (design/auth-v4.html §6) ─────────────────────

export type PhoneVerified = { user: { userId: string; phoneE164: string | null }; moved: boolean };
/** Six digits by SMS to the number; rate-limited, typed refusals (rate_limited, not_a_mobile, sms_failed). */
export const sendPhoneCode = (phone: string) => api.post<{ phoneE164: string; expiresAt: string }>('/auth/phone/codes', { phone });
/** Redeem the code; the device may now speak for another identity (moved) — the stored session follows. */
export async function verifyPhone(phone: string, code: string): Promise<PhoneVerified> {
  const v = await api.post<PhoneVerified>('/auth/phone/verify', { phone, code });
  adoptIdentity(v.user);
  return v;
}

export const putMe = (rid: string, i: MyProfileInput) => api.put<Customer>(`/restaurants/${rid}/customer/me`, i);
export const fetchMyOrders = (rid: string) => api.getMine<Order[]>(`/restaurants/${rid}/customer/me/orders`);
export const fetchMyOrder = (rid: string, orderId: string) => api.getMine<Order>(`/restaurants/${rid}/customer/me/orders/${orderId}`);
export const fetchMyCard = (rid: string) => api.getMine<LoyaltyCard | null>(`/restaurants/${rid}/customer/me/loyalty-card`);
export const putMyAddress = (rid: string, addressId: string, a: AddressInput) => api.put<Customer>(`/restaurants/${rid}/customer/me/addresses/${addressId}`, a);
export const deleteMyAddress = (rid: string, addressId: string) => api.del<Customer>(`/restaurants/${rid}/customer/me/addresses/${addressId}`);
export const fetchBookingConfig = (rid: string, sid: string) => api.get<BookingConfig>(`${storeBase(rid, sid)}/booking-settings`);
export const placeBooking = (rid: string, sid: string, i: PlaceBookingInput) => api.post<Booking>(`${storeBase(rid, sid)}/bookings`, i);
export const fetchMyBookings = (rid: string, sid: string) => api.getMine<Booking[]>(`${storeBase(rid, sid)}/customer/me/bookings`);

// ── small shared helpers ────────────────────────────────────────────

export function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** One line for an address: "1 Smith St, Newtown NSW 2042". */
export function addressText(a: { addressLine: string; suburb: string; state: string; postcode: string }): string {
  return `${a.addressLine}, ${a.suburb} ${a.state} ${a.postcode}`;
}

/** What a public coupon does, in a customer's words. */
export function offerText(c: PublicCoupon): string {
  const what = c.couponType === 'free_delivery' ? 'Free delivery' : c.couponType === 'percent' ? `${(c.percentBp ?? 0) / 100}% off` : `${money(c.amountInCents ?? 0)} off`;
  return c.minOrderInCents ? `${what} on orders of ${money(c.minOrderInCents)} or more` : what;
}

export function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
}

/** An Australian mobile as E.164: "0400 123 456" → "+61400123456". */
export function toE164(raw: string): string | null {
  const p = raw.replace(/[^\d+]/g, '');
  if (/^\+\d{9,15}$/.test(p)) return p;
  if (/^0\d{9}$/.test(p)) return `+61${p.slice(1)}`;
  if (/^61\d{9}$/.test(p)) return `+${p}`;
  return null;
}
